import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "@/components/ui";
import { ItemThumbnail } from "@/components/common";
import { useStorageObjectUrl } from "@/hooks/useStorageObjectUrl";
import { cn } from "@/lib/utils";

interface ReceiptImagePreviewProps {
  fileUrl: string;
  compact?: boolean;
}

export function ReceiptImagePreview({
  fileUrl,
  compact = false,
}: ReceiptImagePreviewProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("components");
  const [previewOpen, setPreviewOpen] = useState(false);
  const storage = useStorageObjectUrl(fileUrl);
  const previewSrc =
    storage.status === "ready" ? storage.url : undefined;
  const previewFailed = storage.status === "error";

  return (
    <>
      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        className={cn(
          "shrink-0 overflow-hidden rounded-lg border border-surface-200 bg-surface-50 ring-1 ring-surface-100 transition hover:ring-primary-200",
          compact ? "h-12 w-12" : "h-16 w-16",
        )}
        aria-label={t("receiptViewFull")}
      >
        <ItemThumbnail
          fill
          imageUrl={fileUrl}
          alt={t("receiptPreviewAlt")}
          className="h-full w-full rounded-lg"
        />
      </button>

      <Modal
        closeLabel={tCommon("close")}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        size="lg"
        showCloseButton
      >
        {previewSrc ? (
          <img
            src={previewSrc}
            alt={t("receiptPreviewAlt")}
            className="mx-auto max-h-[min(70vh,32rem)] w-full object-contain"
          />
        ) : previewFailed ? (
          <div className="flex h-48 items-center justify-center text-sm text-surface-500">
            {t("receiptPreviewAlt")}
          </div>
        ) : (
          <div className="flex h-48 items-center justify-center">
            <Spinner size="lg" loadingLabel={tCommon("loading")} />
          </div>
        )}
      </Modal>
    </>
  );
}
